// Shared nurse:patient staffing-ratio logic — used by WardRatioStrip.js (live
// ward staffing display) and the leave-approval impact check
// (LeaveApprovalsSection.js). No React/MUI dependency: this module is pure
// data transforms only, so both a display component and a plain-JS check can
// use it without pulling in rendering concerns.
//
// Staffing thresholds, in patients per nurse. LIMIT is the line a ward is
// over; SAFE is the line below which it's comfortable. Change them here only
// — nowhere else in the codebase should redefine these numbers.
export const SAFE = 4;
export const LIMIT = 6;

// nurses === 0 is "unstaffed" regardless of ratio (there is no ratio without
// a denominator). Otherwise compare the occupied:nurses ratio against LIMIT
// then SAFE.
export function statusOf(nurses, ratio) {
  if (nurses === 0) return "unstaffed";
  if (ratio > LIMIT) return "over";
  if (ratio > SAFE) return "stretched";
  return "adequate";
}

// Groups census beds + roster rows into one entry per occupied ward. A ward is a
// room category — ICU / General / Private — and nothing else. It used to be the
// (department_id, room_category_code) pair, which meant one physical ICU shared by
// Cardiology and Orthopaedics produced two ward entries with two separate ratios.
//
// This is also why the department could not simply be dropped from the key without
// changing the guard below: beds no longer carry a department at all, so the old
// `if (!b.department_id) return` skipped EVERY bed and this function returned an
// empty list — blanking the ratio strip and silently disabling the leave-approval
// staffing floor, with no error raised anywhere.
//
// rosterRows should already be filtered to whichever date(s)/shift(s) the caller
// cares about — this function has no notion of "today" or "the live shift". Returns
// UNSORTED entries; sorting (e.g. worst-first for display) is the caller's concern.
export function buildWards(beds, rosterRows) {
  const map = new Map();
  beds.forEach((b) => {
    if (!b.room_category_code) return;
    const key = b.room_category_code;
    if (!map.has(key)) {
      map.set(key, {
        wardCode: b.room_category_code,
        wardName: b.room_category_name || b.room_category_code,
        occupied: 0,
        nurses: 0,
      });
    }
    const w = map.get(key);
    if ((b.status || "").toLowerCase() === "occupied") w.occupied += 1;
  });
  rosterRows.forEach((r) => {
    if (!["on_duty", "overtime"].includes(r.status)) return;
    const w = map.get(r.ward_code);
    if (w) w.nurses += 1;
  });
  return [...map.values()]
    .filter((w) => w.occupied > 0)
    .map((w) => {
      const ratio = w.nurses > 0 ? w.occupied / w.nurses : null;
      return { ...w, ratio, statusKey: statusOf(w.nurses, ratio ?? 0) };
    });
}

// The shifts the roster/ratio model recognizes now come from the facility's
// schedule — use the codes on the schedule useShiftSchedule() returns.

// What approving this nurse's leave TODAY would do to ward staffing.
// todayRosterRows must already be scoped to today (the caller's job — e.g.
// via getWardRoster({from_date: today, to_date: today}), same as
// WardRatioStrip and WeekGrid already do). This function has no notion of
// "today" itself.
//
// For each shift the nurse is on_duty/overtime in today, simulates removing
// their row(s) and recomputes the ward(s) they were covering. An entry is
// returned whenever the RESULTING status is worse than "adequate" —
// evaluated on the after-state alone, not as a delta from before, so a ward
// that's already Stretched (e.g. from an earlier approval today) and stays
// Stretched after this nurse's leave too still returns an entry. Wards with
// no occupied beds are never returned (buildWards already excludes them —
// there's no ratio to protect in an empty ward).
export function computeLeaveImpact(nurseId, beds, todayRosterRows) {
  const impacts = [];
  // The shifts to consider are whichever ones actually appear in the day's
  // roster — derived from the rows rather than from a fixed list, since the
  // facility now defines its own shifts and this function has no schedule.
  const shifts = Array.from(new Set(todayRosterRows.map((r) => r.shift).filter(Boolean)));
  shifts.forEach((shift) => {
    const shiftRows = todayRosterRows.filter((r) => r.shift === shift);
    const nurseRowsThisShift = shiftRows.filter(
      (r) => r.nurse_id === nurseId && ["on_duty", "overtime"].includes(r.status)
    );
    if (nurseRowsThisShift.length === 0) return;

    const nurseWardKeys = new Set(nurseRowsThisShift.map((r) => r.ward_code));
    const beforeWards = buildWards(beds, shiftRows);
    const afterWards = buildWards(beds, shiftRows.filter((r) => r.nurse_id !== nurseId));

    afterWards.forEach((afterWard) => {
      const key = afterWard.wardCode;
      if (!nurseWardKeys.has(key)) return; // this ward is unrelated to this nurse's leave
      if (afterWard.statusKey === "adequate") return;
      const beforeWard = beforeWards.find((w) => w.wardCode === key);
      impacts.push({
        shift,
        wardName: afterWard.wardName,
        occupied: afterWard.occupied,
        before: {
          nurses: beforeWard?.nurses ?? 0,
          ratio: beforeWard?.ratio ?? null,
          statusKey: beforeWard?.statusKey ?? "unstaffed",
        },
        after: { nurses: afterWard.nurses, ratio: afterWard.ratio, statusKey: afterWard.statusKey },
      });
    });
  });
  return impacts;
}
